import type { AlertProps } from '../../../../../core'
import { Button, Money } from '../../../../../core'
import { useEncounterIdRouteParam } from '../../hooks/useEncounterIdRouteParam'
import type { ActionSubpath } from '../actions/encounterActionTypes'
import { EncounterLink } from '../EncounterLink'
import { EncounterSnoozeAcknowledgementButton } from '../snooze/EncounterSnoozeAcknowledgementButton'
import type { components } from '@joyful/openapi/types.gen'
import { cn, formatDate, pluralize, createEnumSchema } from '@joyful/utils'
import {
  DollarSignIcon,
  CircleAlertIcon,
  TriangleAlertIcon,
} from 'lucide-react'
import type { ComponentType, ReactElement, SVGProps } from 'react'
import type { EnumValueMeta } from '@joyful/utils'

function TakeActionButton({
  action = null,
  children,
}: {
  action?: ActionSubpath | null
  children?: string
}) {
  const encounterId = useEncounterIdRouteParam()

  return (
    <Button variant="ghost" size="sm" asChild>
      <EncounterLink
        encounterId={encounterId}
        queryParams={{
          action,
          actionId: null,
          intent: null,
          sidebar: 'action',
        }}
      >
        {children ?? `Take action`}
      </EncounterLink>
    </Button>
  )
}

type UrgencyIndicator = NonNullable<
  components['schemas']['EncounterListItem']['urgency_indicator']
>

type Metadata = {
  [K in UrgencyIndicator['reason']]: {
    actions:
      | ReactElement
      | Array<ReactElement>
      | ((params: {
          encounter: Pick<
            components['schemas']['EncounterAPIResponse'],
            'id' | 'assignee_biller_user_id'
          >
        }) => ReactElement | Array<ReactElement>)
    description:
      | string
      | ReactElement
      | ((params: {
          encounter: Pick<components['schemas']['EncounterAPIResponse'], 'id'>
          indicator: Extract<UrgencyIndicator, { reason: K }>
        }) => string | ReactElement)
    icon: ComponentType<SVGProps<SVGSVGElement>>
    title?: string
    variant: NonNullable<AlertProps['variant']>
  }
}

export const EncounterUrgencyIndicatorMetadata: Metadata = {
  deadline_critical: {
    actions: (
      <TakeActionButton action="research">Log research</TakeActionButton>
    ),
    description: ({ indicator }) => {
      return (
        <p>
          {indicator.days_until_deadline === 0 ? (
            `This encounter has hit the timely filing deadline.`
          ) : (
            <>
              {`There are only `}
              <strong>{pluralize(indicator.days_until_deadline, 'day')}</strong>
              {` until the timely filing deadline.`}
            </>
          )}
          {` Please follow up with the payer immediately.`}
        </p>
      )
    },
    icon: CircleAlertIcon,
    title: 'Deadline approaching',
    variant: 'error',
  },

  deadline_warning: {
    actions: (
      <TakeActionButton action="research">Log research</TakeActionButton>
    ),
    description: ({ indicator }) => {
      return (
        <p>
          {`There are `}
          <strong>{pluralize(indicator.days_until_deadline, 'day')}</strong>
          {` until the timely filing deadline. Please follow up with the payer before the timely filing deadline.`}
        </p>
      )
    },
    icon: TriangleAlertIcon,
    variant: 'warning',
  },

  denial_upheld: {
    actions: [
      <TakeActionButton
        key="resubmission"
        action="submitted:claim-resubmission"
      >
        {`Log resubmission`}
      </TakeActionButton>,
      <TakeActionButton key="escalate" action="escalate">
        Escalate
      </TakeActionButton>,
    ],
    description: `The denial for this encounter has been upheld. Please resubmit with corrections or escalate to a supervisor.`,
    icon: CircleAlertIcon,
    title: 'Denial upheld',
    variant: 'error',
  },

  multiple_failures: {
    actions: (
      <TakeActionButton action="escalate">{`Escalate`}</TakeActionButton>
    ),
    description: ({ indicator }) => {
      return (
        <p>
          {`This encounter has `}
          <strong>
            {[
              indicator.denial_count > 0 &&
                pluralize(indicator.denial_count, 'denial'),
              indicator.rejection_count > 0 &&
                pluralize(indicator.rejection_count, 'rejection'),
            ]
              .filter(Boolean)
              .join(' and ')}
          </strong>
          {`. Please escalate to a supervisor.`}
        </p>
      )
    },
    icon: TriangleAlertIcon,
    variant: 'warning',
  },

  payment_received: {
    actions: (
      <TakeActionButton action="resolve:found-money">
        Resolve claim
      </TakeActionButton>
    ),
    description: ({ indicator }) => {
      return (
        <p>
          {indicator.num_payments === 1 ? (
            <>
              {`A payment of `}
              <Money asChild>
                <strong>{indicator.payment_amount}</strong>
              </Money>
              {` was received.`}
            </>
          ) : (
            <>
              {`${pluralize(indicator.num_payments, 'payment')} totaling `}
              <Money asChild>
                <strong>{indicator.payment_amount}</strong>
              </Money>
              {` were received.`}
            </>
          )}
          {` Please mark as resolved and post payment if necessary.`}
        </p>
      )
    },
    icon: DollarSignIcon,
    title: 'Payment received',
    variant: 'info',
  },

  rejection_upheld: {
    actions: [
      <TakeActionButton
        key="resubmission"
        action="submitted:claim-resubmission"
      >
        Log resubmission
      </TakeActionButton>,
      <TakeActionButton key="escalate" action="escalate">
        Escalate
      </TakeActionButton>,
    ],
    description: `The rejection for this encounter has been upheld. Please resubmit with corrections or escalate to a supervisor.`,
    icon: CircleAlertIcon,
    title: 'Rejection upheld',
    variant: 'error',
  },

  snooze_expired: {
    actions: ({ encounter }) => {
      return <EncounterSnoozeAcknowledgementButton encounter={encounter} />
    },
    description: ({ indicator }) => {
      return `This claim was snoozed until ${formatDate(
        indicator.snoozed_until,
        {
          shouldSkipCurrentYear: true,
        },
      )}. The snooze has expired.`
    },
    icon: BlueCircleIcon,
    variant: 'neutral',
  },

  stale_in_progress: {
    actions: <TakeActionButton />,
    description: ({ indicator }) => {
      return (
        <p>
          {`This encounter has been in progress for `}
          <strong>{pluralize(indicator.days_in_progress, 'day')}</strong>
          {` . Please review and take action.`}
        </p>
      )
    },
    icon: TriangleAlertIcon,
    title: 'Stale in progress encounter',
    variant: 'warning',
  },

  stale_pending: {
    actions: (
      <TakeActionButton action="research">Log research</TakeActionButton>
    ),
    description: ({ indicator }) => {
      return (
        <p>
          {`This encounter was marked as pending `}
          <strong>{pluralize(indicator.days_in_pending, 'day')}</strong>
          {` ago. Please follow up with the payer.`}
        </p>
      )
    },
    icon: TriangleAlertIcon,
    variant: 'warning',
  },
}

export function getUrgencyIndicatorMetadata(
  reason: UrgencyIndicator['reason'] | undefined,
) {
  if (reason === undefined) {
    return undefined
  }

  return EncounterUrgencyIndicatorMetadata[reason]
}

function BlueCircleIcon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      className={cn(
        'mr-0.5 size-3.5! fill-blue-500 stroke-transparent',
        className,
      )}
      {...props}
    >
      <circle cx={12} cy={12} r={10} />
    </svg>
  )
}

interface EncounterPaymentUpdateTypeMetadata extends EnumValueMeta {
  icon: ComponentType<SVGProps<SVGSVGElement>>
  variant: NonNullable<AlertProps['variant']>
}

export const EncounterPaymentUpdateTypeSchema = createEnumSchema<
  components['schemas']['EncounterPaymentUpdateType'],
  EncounterPaymentUpdateTypeMetadata
>([
  {
    icon: EncounterUrgencyIndicatorMetadata.denial_upheld.icon,
    title: 'Denial upheld',
    value: 'DENIAL_UPHELD',
    variant: EncounterUrgencyIndicatorMetadata.denial_upheld.variant,
  },
  {
    icon: EncounterUrgencyIndicatorMetadata.payment_received.icon,
    title: 'Payment received',
    value: 'PAYMENT_RECEIVED',
    variant: EncounterUrgencyIndicatorMetadata.payment_received.variant,
  },
  {
    icon: EncounterUrgencyIndicatorMetadata.rejection_upheld.icon,
    title: 'Rejection upheld',
    value: 'REJECTION_UPHELD',
    variant: EncounterUrgencyIndicatorMetadata.rejection_upheld.variant,
  },
])
